import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Search, Download, FileText, BookOpen, BarChart2, FileSpreadsheet, Filter } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const allResources = [{
  title: 'National Agriculture Policy',
  category: 'Policy Document',
  size: '2.4 MB PDF',
  date: '2013',
  icon: FileText
}, {
  title: 'Agricultural Sector Strategic Plan (ASSP) 2020/21 - 2024/25',
  category: 'Strategic Plan',
  size: '5.1 MB PDF',
  date: '2020',
  icon: BookOpen
}, {
  title: 'Annual Agricultural Sector Performance Report 2022/2023',
  category: 'Annual Report',
  size: '8.7 MB PDF',
  date: '2023',
  icon: BarChart2
}, {
  title: 'National Coffee Act, 2021',
  category: 'Legislation',
  size: '1.2 MB PDF',
  date: '2021',
  icon: FileText
}, {
  title: 'Guidelines for Registration of Agrochemicals',
  category: 'Guidelines',
  size: '3.5 MB PDF',
  date: '2022',
  icon: FileSpreadsheet
}, {
  title: 'National Fertilizer Policy',
  category: 'Policy Document',
  size: '1.8 MB PDF',
  date: '2016',
  icon: FileText
}, {
  title: 'Uganda Climate Smart Agriculture Guidelines',
  category: 'Guidelines',
  size: '4.2 MB PDF',
  date: '2021',
  icon: BookOpen
}, {
  title: 'Fisheries and Aquaculture Act, 2022',
  category: 'Legislation',
  size: '2.1 MB PDF',
  date: '2022',
  icon: FileText
}, {
  title: 'National Irrigation Policy',
  category: 'Policy Document',
  size: '2.9 MB PDF',
  date: '2017',
  icon: FileText
}, {
  title: 'Agricultural Extension Guidelines',
  category: 'Guidelines',
  size: '3.1 MB PDF',
  date: '2019',
  icon: BookOpen
}, {
  title: 'Livestock Disease Control Manual',
  category: 'Manual',
  size: '6.5 MB PDF',
  date: '2020',
  icon: BookOpen
}, {
  title: 'Quarterly Market Price Report - Q3 2023',
  category: 'Report',
  size: '1.5 MB PDF',
  date: '2023',
  icon: BarChart2
}];
const categories = ['All', 'Policy Document', 'Strategic Plan', 'Legislation', 'Guidelines', 'Annual Report', 'Manual', 'Report'];
export function ResourcesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredResources = allResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || resource.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  return <>
      <Header data-id="element-1433" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1434">
        {/* Hero Section */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1435">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1592982537447-6f2a6a0c7c18?w=1600&h=600&fit=crop")'
        }} data-id="element-1436"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1437"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1438">
            {/* Breadcrumb */}
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1439">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1440">
                <Home className="w-4 h-4 mr-1" data-id="element-1441" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1442" />
              <span data-id="element-1443">Resources</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1444" />
              <span className="text-white font-medium" data-id="element-1445">Knowledge Hub</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1446">
              Knowledge Hub & Resources
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed mb-8" data-id="element-1447">
              Access official publications, research data, agricultural
              guidelines, policy documents, and legislation from the Ministry of
              Agriculture, Animal Industry and Fisheries.
            </p>

            {/* Search Bar */}
            <div className="max-w-3xl relative" data-id="element-1448">
              <input type="text" placeholder="Search for documents, guidelines, reports..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 rounded-lg text-uganda-black focus:outline-none focus:ring-2 focus:ring-uganda-yellow shadow-lg" data-id="element-1449" />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-6 h-6" data-id="element-1450" />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1451">
          <div className="flex flex-col md:flex-row gap-8" data-id="element-1452">
            {/* Sidebar Filters */}
            <div className="w-full md:w-64 flex-shrink-0" data-id="element-1453">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-32" data-id="element-1454">
                <div className="flex items-center gap-2 mb-6 text-uganda-black font-bold text-lg" data-id="element-1455">
                  <Filter className="w-5 h-5 text-uganda-green" data-id="element-1456" />
                  Categories
                </div>
                <div className="space-y-2" data-id="element-1457">
                  {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeCategory === category ? 'bg-uganda-light-green text-uganda-green border border-green-200' : 'text-gray-600 hover:bg-gray-50 hover:text-uganda-black'}`} data-id="element-1458">
                      {category}
                    </button>)}
                </div>
              </div>
            </div>

            {/* Resources Grid */}
            <div className="flex-1" data-id="element-1459">
              <div className="mb-6 flex justify-between items-center" data-id="element-1460">
                <h2 className="text-xl font-bold text-uganda-black" data-id="element-1461">
                  {activeCategory === 'All' ? 'All Documents' : `${activeCategory}s`}
                </h2>
                <span className="text-sm text-gray-500 font-medium bg-gray-100 px-3 py-1 rounded-full" data-id="element-1462">
                  {filteredResources.length} Results
                </span>
              </div>

              {filteredResources.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1463">
                  {filteredResources.map((resource, index) => {
                const Icon = resource.icon;
                return <motion.div key={index} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.3,
                  delay: index * 0.05
                }} className="bg-white rounded-xl p-6 text-uganda-black flex flex-col h-full shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-1464">
                        <div className="flex items-start justify-between mb-4" data-id="element-1465">
                          <div className="w-12 h-12 rounded-lg bg-green-50 text-uganda-green flex items-center justify-center flex-shrink-0" data-id="element-1466">
                            <Icon className="w-6 h-6" data-id="element-1467" />
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded" data-id="element-1468">
                            {resource.category}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg mb-3 flex-grow leading-snug" data-id="element-1469">
                          {resource.title}
                        </h3>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4" data-id="element-1470">
                          <span data-id="element-1471">{resource.date}</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full" data-id="element-1472" />
                          <span data-id="element-1473">{resource.size}</span>
                        </div>
                        <div className="pt-4 border-t border-gray-100" data-id="element-1474">
                          <button className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-uganda-green hover:text-uganda-green text-gray-700 py-2 rounded-lg text-sm font-semibold transition-colors" data-id="element-1475">
                            <Download className="w-4 h-4" data-id="element-1476" /> Download PDF
                          </button>
                        </div>
                      </motion.div>;
              })}
                </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-1477">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1478" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1479">
                    No resources found
                  </h3>
                  <p className="text-gray-500" data-id="element-1480">
                    Try adjusting your search or category filter.
                  </p>
                  <button onClick={() => {
                setSearchQuery('');
                setActiveCategory('All');
              }} className="mt-6 text-uganda-green font-semibold hover:underline" data-id="element-1481">
                    Clear all filters
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1482" />
    </>;
}